import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useApiClient } from './client.jsx';
import { useFixturesMode, fixtureDelay } from './fixtures';
import { isApiError } from './errors';

/**
 * Fixture-aware mutation helper. Encapsuliert das wiederkehrende Pattern:
 *
 *  - im Fixture-Mode → künstliche Latenz + lokaler Fake-Return
 *  - sonst → echter API-Call mit `fetch`
 *  - automatische Query-Invalidierung über `invalidate`-Keys
 *  - Toast bei Success (mit dynamic-message-Helper)
 *  - Toast bei Fehler mit Error-Code-Awareness (siehe error-handling.md)
 *
 * @template TPayload, TResult
 * @param {Object} opts
 * @param {(fetch: import('ofetch').$Fetch, payload: TPayload) => Promise<TResult>} opts.live
 *   Live-API-Call. Erhält den fetch-Wrapper als ersten Arg.
 * @param {(payload: TPayload) => TResult | Promise<TResult>} [opts.fixture]
 *   Optional. Fixture-Return. Default: `{ ok: true }`.
 * @param {Array<string | (string | number)[]>} [opts.invalidate]
 *   QueryKeys die nach Success invalidiert werden.
 * @param {string | ((result: TResult, payload: TPayload) => string)} [opts.successMessage]
 *   Toast-Text bei Success. Wenn leer → keine Success-Toast.
 * @param {(err: import('./errors').ApiError) => string} [opts.errorMessage]
 *   Custom Error-Renderer. Default: `err.message ?? 'Unbekannter Fehler'`.
 */
export function useApiMutation({
  live,
  fixture,
  invalidate = [],
  successMessage,
  errorMessage,
}) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      if (fixturesMode) {
        await fixtureDelay(200);
        return fixture ? fixture(payload) : { ok: true };
      }
      return live(fetch, payload);
    },
    onSuccess: (result, payload) => {
      invalidate.forEach((key) => {
        const queryKey = Array.isArray(key) ? key : [key];
        qc.invalidateQueries({ queryKey });
      });
      if (successMessage) {
        const msg =
          typeof successMessage === 'function'
            ? successMessage(result, payload)
            : successMessage;
        if (msg) toast.success(msg);
      }
    },
    onError: (err) => {
      const fallback = isApiError(err)
        ? err.message
        : err?.message ?? 'Unbekannter Fehler';
      const msg = errorMessage ? errorMessage(err) ?? fallback : fallback;
      toast.error(msg);
    },
  });
}
